// REST client following contracts/rest-api.md: problem+json errors, CSRF token,
// credentials included (MPass session cookie).

import { fileNameFromDisposition } from '@/services/contentDisposition';
import type { DownloadedFile } from '@/services/types/DownloadedFile';
import type { Problem } from '@/services/types/Problem';
import type { UnauthorizedHandler } from '@/services/types/UnauthorizedHandler';
import { withLanguageParam } from '@/services/languageParam';

export class ApiError extends Error {
  constructor(public readonly problem: Problem) {
    super(problem.title ?? `HTTP ${problem.status}`);
  }
}

/** The server's own sentence for a failure — an ApiError's `problem.detail` — or null. */
export function problemDetail(e: unknown): string | null {
  return e instanceof ApiError && e.problem.detail ? e.problem.detail : null;
}

/**
 * The refusal's key — `training.session.end_before_start` — that every keyed refusal carries in
 * problem+json (TR-2), or null for a refusal written as a sentence and for anything not an ApiError.
 * What a screen branches on, where the sentence in `detail` is what it shows.
 */
export function problemCode(e: unknown): string | null {
  return e instanceof ApiError && typeof e.problem.code === 'string' ? e.problem.code : null;
}

/**
 * The server's own words for a failure where it has any, and the screen's sentence where it has
 * not: a 409 that names the dependency blocking a delete says more than any wording composed here.
 */
export function detailOr(e: unknown, fallback: string): string {
  return problemDetail(e) ?? fallback;
}

/** Whether `e` is an ApiError answered with one of `statuses`. */
export function isProblemStatus(e: unknown, ...statuses: number[]): e is ApiError {
  return e instanceof ApiError && statuses.includes(e.problem.status);
}

/**
 * The CSRF token this session's cookie carries, or undefined before one is issued.
 *
 * Exported for the page-view report, which is a bare fetch rather than one of the calls below —
 * telemetry must not reach the session-expiry handler (see reportPageView) — and which still has to
 * send the token, because the cabinet's endpoint is not under the exempt `/api/v1/public/**`.
 */
export function csrfToken(): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];
}

let unauthorized: UnauthorizedHandler | null = null;
let forbidden: ((refusal: { url: string; problem: Problem }) => void) | null = null;

/**
 * Register what should happen when the backend answers 403 — the administration's "change your
 * temporary password first" refusal, in practice (ADM-1; services/adminSession.ts decides). Like
 * the 401 hook, api.ts only reports, with the address the refusal came from.
 */
export function setForbiddenHandler(
  handler: ((refusal: { url: string; problem: Problem }) => void) | null,
): void {
  forbidden = handler;
}

/**
 * Register what should happen when the backend answers 401 — session expiry, in practice.
 *
 * api.ts only *reports*; it knows nothing about React, window.location or sessionStorage.
 * SessionProvider installs the handler and owns the policy. Passing null removes it, which is
 * how the mount-time "not signed in yet" bootstrap stays exempt: while there is no user there
 * is no handler, so /api/v1/me's 401 rejects exactly as it always did and RequireSession keeps
 * owning that decision.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorized = handler;
}

/**
 * The one parse-problem-and-throw path, shared by request(), apiDownload and apiMultipart — they
 * used to carry byte-identical copies of it, so a 401 hook added to one would have missed the
 * others. Notably the media upload is the *first* leg of NewsAdminPage's save handler, which is
 * where the worst unsaved-work loss lives.
 *
 * Keys off `response.status`, not `problem.status`: the transport status is the authority, and
 * a body can disagree with it (or fail to parse at all, as the catch below allows).
 */
async function fail(response: Response, method: string): Promise<never> {
  let problem: Problem;
  try {
    const body = (await response.json()) as Problem;
    // The transport status where the body does not carry one. A problem+json body normally
    // repeats it, but nothing guarantees it — and a caller asking `problem.status === 404` was
    // silently answered `undefined` for those, which reads as "some other failure" rather than
    // as the status the server actually sent. Only filled in, never overridden: a body that
    // states its own status is left saying what it says.
    problem = { ...body, status: body.status ?? response.status };
  } catch {
    problem = { status: response.status, title: response.statusText };
  }
  // Status alone, never the body. All three backend 401 sources (the anonymous entry point,
  // AccountStatusFilter's "Contul nu mai este activ", StatsApi's API-key gate) want the same
  // action, their titles are untranslated Romanian prose that the live i18n work could change
  // under us, and none of them populates the machine-readable `type`.
  if (response.status === 401) unauthorized?.({ method, problem });
  if (response.status === 403) forbidden?.({ url: response.url, problem });
  throw new ApiError(problem);
}

let currentLanguage: string | null = null;

/**
 * Tell the API layer which language the UI is showing, so public reads and the session request can
 * carry it (withLanguageParam).
 *
 * The backend resolves content language from the HTTP session, not from Accept-Language —
 * LocaleConfig wires a SessionLocaleResolver with an explicit default, and its javadoc says the
 * header is never consulted. The switch is a `lang` request parameter, picked up by a
 * LocaleChangeInterceptor. That worked for a server-rendered site, where changing language WAS a
 * navigation; a React zone changes it without a request, so the server would keep serving
 * Romanian to a visitor reading English and, worse, keep reporting languageFallback=false — the
 * "this page is not available in English" notice would never appear.
 *
 * Set from LanguageProvider rather than read from a context here, for the same reason the 401
 * handler is registered rather than imported: this module stays React-free so it can be used
 * from anywhere, including code that runs before React mounts.
 */
export function setApiLanguage(language: string | null): void {
  currentLanguage = language;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const token = csrfToken();
  if (token && method !== 'GET') headers['X-XSRF-TOKEN'] = token;

  const response = await fetch(withLanguageParam(method, url, currentLanguage), {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) return fail(response, method);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const apiGet = <T>(url: string) => request<T>('GET', url);
export const apiPost = <T>(url: string, body?: unknown) => request<T>('POST', url, body);
export const apiPut = <T>(url: string, body?: unknown) => request<T>('PUT', url, body);
export const apiPatch = <T>(url: string, body?: unknown) => request<T>('PATCH', url, body);
export const apiDelete = <T>(url: string) => request<T>('DELETE', url);

/**
 * A file to keep, fetched rather than linked.
 *
 * `<a download href="/api/…">` is the shorter way to write this and the wrong one: the browser
 * follows the link outside this client, so a 403 or a 404 is not an error anybody sees — it is a
 * successful save of the problem+json body, under the name the anchor promised. The reader ends up
 * with `cerere-2026-000123.txt` containing `{"status":403,…}` and no sign that anything went
 * wrong. Going through fetch is what puts the status back in the caller's hands, and it also
 * carries the session cookie and the 401 handler that every other request here gets.
 *
 * Returns the bytes and the name the server chose — see contentDisposition.ts — leaving the saving
 * itself to the caller, which is the one part of this that needs a DOM.
 */
export async function apiDownload(url: string): Promise<DownloadedFile> {
  const response = await fetch(url, {
    method: 'GET',
    // Not `application/json`: the answer is a document, and an Accept the endpoint cannot satisfy
    // is a 406 in place of the file. The errors still arrive as problem+json, which `fail` parses
    // whatever was asked for.
    headers: { Accept: '*/*' },
    credentials: 'include',
  });
  if (!response.ok) return fail(response, 'GET');
  return {
    blob: await response.blob(),
    fileName: fileNameFromDisposition(response.headers.get('Content-Disposition')),
  };
}

/**
 * A multipart POST — the media store's upload, the news import. The caller builds the FormData,
 * since each endpoint names its own parts; no Content-Type is set, so the browser writes the
 * boundary into it.
 */
export async function apiMultipart<T>(url: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = csrfToken();
  if (token) headers['X-XSRF-TOKEN'] = token;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
  });
  if (!response.ok) return fail(response, 'POST');
  return (await response.json()) as T;
}
